/**
 * Menu-item detail data layer — backs the order-table item-detail modal.
 *
 * `GET /api/menus/item/:menuItemId` returns one item's customer-facing detail:
 * description, image, dietary tags, allergens, ingredient summary, per-serving
 * nutrition facts, and serving-size label. Fetched lazily — only enabled once
 * an item is selected (the modal passes the clicked item's MenuItem id).
 */

import type { NutritionFacts } from '@ordo/constants/nutrition'
import { useQuery } from '@tanstack/react-query'

import { apiFetch } from './http'

export interface MenuItemDetailAllergen {
  id: string
  name: string
}

export interface MenuItemDetail {
  id: string
  itemKind: 'BUNDLE' | 'COMPONENT'
  title: string
  description: string | null
  imageUri: string | null
  componentKind: string | null
  dietaryTags: string[]
  allergens: MenuItemDetailAllergen[]
  ingredients: string | null
  /** Per-serving facts; for a BUNDLE, the whole meal's total ("1 meal"). */
  nutrition: NutritionFacts | null
  /**
   * BUNDLE only: the meal's components the nutrition total leaves out (no
   * nutrition data). Optional for older cached payloads; empty = nothing left out.
   */
  nutritionExcludes?: string[]
  servingSizeLabel: string | null
}

/**
 * `serviceDate` (`YYYY-MM-DD`) is the day the modal was opened from: the
 * detail then describes the item AS SERVED that day — a dated substitution
 * rule swaps what the row names for the days it covers (PROD-1386).
 */
export async function fetchMenuItemDetail(
  menuItemId: string,
  serviceDate?: string | null,
  signal?: AbortSignal,
): Promise<MenuItemDetail> {
  const { item } = await apiFetch<{ item: MenuItemDetail }>(`/api/menus/item/${encodeURIComponent(menuItemId)}`, {
    query: { serviceDate: serviceDate ?? undefined },
    signal,
  })
  return item
}

export function useMenuItemDetailQuery(menuItemId: string | null, serviceDate?: string | null) {
  return useQuery<MenuItemDetail>({
    queryKey: ['menus', 'item', menuItemId, serviceDate ?? null],
    queryFn: ({ signal }) => fetchMenuItemDetail(menuItemId as string, serviceDate, signal),
    enabled: Boolean(menuItemId),
  })
}
